import { useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, ShieldCheck, Sparkles, X } from "lucide-react";
import { Button, Card, PageLoader } from "@/components/ui";
import { api, ApiError } from "@/lib/api";

interface ConnectRequest {
  clientName: string | null;
  assistant: { name: string; company: string } | null;
  redirectHost: string;
  canRequestChanges: boolean;
  familyName: string;
}

const sees = (names: boolean) =>
  `See your family's setup: ${names ? "kids' first names and ages" : "kids' ages (shown as Kid 1, Kid 2…)"}, responsibilities, jobs, rewards and limits`;
const CHANGES =
  "Add kids and change family and kid settings, responsibilities, jobs, rewards and screen-time limits, store items and consequences (as you ask it to). Tasks it recommends wait for your approval";
const USAGE = "See weekly counts of tasks each kid finished and how often each task gets done, so it can tell you how things are going";
const cant = (usage: boolean) => [
  `See ${usage ? "individual activity entries" : "activity history"}, notes, balances or anything your kids type`,
  "See passwords, PINs or payment details",
  "Approve or pay out kids' work, or reach any other family",
];

/** OAuth consent: a parent lets an assistant (e.g. Claude) configure the family. */
export function ConnectPage() {
  const [params] = useSearchParams();
  const id = params.get("request") ?? "";
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);
  const [access, setAccess] = useState<"write" | "read">("write");
  const [shareKidNames, setShareKidNames] = useState(false);
  const [shareUsage, setShareUsage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const req = useQuery<ConnectRequest, ApiError>({
    queryKey: ["oauth-request", id],
    queryFn: () => api.get<ConnectRequest>(`/oauth/requests/${encodeURIComponent(id)}`),
    enabled: !!id,
    retry: false,
  });

  async function decide(choice: "allow" | "deny") {
    setBusy(choice);
    setError(null);
    try {
      const { redirectUrl } = await api.post<{ redirectUrl: string }>(
        `/oauth/requests/${encodeURIComponent(id)}/${choice === "allow" ? "approve" : "deny"}`,
        choice === "allow" ? { access, shareKidNames, shareUsage } : undefined,
      );
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
      setBusy(null);
    }
  }

  if (id && req.isLoading) return <PageLoader />;
  const app = req.data?.assistant?.name || req.data?.clientName || "This app";

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-[calc(env(safe-area-inset-top)+28px)]">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-9" />
          <span className="font-black tracking-tight">Meaningful Rewards</span>
        </div>

        {!req.data ? (
          <Card className="mt-10 text-center">
            <p className="text-lg font-extrabold">This connection link has expired</p>
            <p className="mt-1 text-muted">Go back to your assistant and connect Meaningful Rewards again.</p>
          </Card>
        ) : (
          <>
            <div className="mt-10 flex justify-center">
              <span className="flex size-16 items-center justify-center rounded-3xl bg-violet-soft text-violet">
                <Sparkles className="size-8" />
              </span>
            </div>
            <h1 className="mt-5 text-center text-2xl font-black leading-tight">
              Connect {app} to {req.data.familyName}?
            </h1>
            <p className="mt-2 text-center text-sm text-muted">
              After you choose, you'll go back to <b className="text-ink">{req.data.redirectHost}</b>. Only allow apps you started connecting yourself.
            </p>

            {req.data.canRequestChanges && (
              <div className="mt-6 grid gap-2" role="radiogroup" aria-label="Access">
                {(
                  [
                    ["write", "View and make changes", "Set up and update your family by chat; suggested tasks wait for your approval"],
                    ["read", "View only", "It can explain and suggest; you make changes in the app"],
                  ] as const
                ).map(([value, title, text]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={access === value}
                    onClick={() => setAccess(value)}
                    className={`rounded-2xl bg-card p-3 text-left shadow-card ring-2 ${access === value ? "ring-brand" : "ring-transparent"}`}
                  >
                    <span className="block font-extrabold">{title}</span>
                    <span className="block text-sm text-muted">{text}</span>
                  </button>
                ))}
              </div>
            )}

            <label className="mt-4 flex items-start gap-3 rounded-2xl bg-card p-3 shadow-card">
              <input
                type="checkbox"
                className="mt-1 size-5 shrink-0 accent-brand"
                checked={shareKidNames}
                onChange={(e) => setShareKidNames(e.target.checked)}
              />
              <span>
                <span className="block font-extrabold">Share my kids' first names</span>
                <span className="block text-sm text-muted">
                  Off: {app} sees your kids as Kid 1, Kid 2… with their ages. Turn on so it can use their names.
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-3 rounded-2xl bg-card p-3 shadow-card">
              <input type="checkbox" className="mt-1 size-5 shrink-0 accent-brand" checked={shareUsage} onChange={(e) => setShareUsage(e.target.checked)} />
              <span>
                <span className="block font-extrabold">Share weekly usage counts</span>
                <span className="block text-sm text-muted">
                  Off: {app} can't tell how the app is being used. Turn on for feedback on what's working. Counts only, never notes or balances.
                </span>
              </span>
            </label>

            <Card className="mt-4 space-y-2">
              <p className="font-extrabold">It will be able to</p>
              {[sees(shareKidNames), ...(shareUsage ? [USAGE] : []), ...(access === "write" && req.data.canRequestChanges ? [CHANGES] : [])].map((c) => (
                <p key={c} className="flex gap-2 text-sm">
                  <Check className="mt-0.5 size-4 shrink-0 text-brand" /> {c}
                </p>
              ))}
              <p className="pt-2 font-extrabold">It won't be able to</p>
              {cant(shareUsage).map((c) => (
                <p key={c} className="flex gap-2 text-sm">
                  <X className="mt-0.5 size-4 shrink-0 text-danger" /> {c}
                </p>
              ))}
            </Card>
            <p className="mt-3 flex items-start gap-2 text-xs text-muted">
              <ShieldCheck className="size-4 shrink-0" />
              <span>
                What {app} reads is shared with {req.data.assistant ? req.data.assistant.company : "its provider"} and handled under your own account with
                them, not by Meaningful Rewards. This is optional, every parent is notified, and each action shows in Settings → Privacy &amp; security.
                Disconnect anytime under Settings → AI assistant &amp; integrations.
              </span>
            </p>

            {error && <p className="mt-4 text-center text-sm font-semibold text-danger">{error}</p>}
            <div className="mt-auto grid gap-3 pt-8">
              <Button size="lg" block onClick={() => decide("allow")} loading={busy === "allow"} disabled={!!busy}>
                Allow
              </Button>
              <Button size="lg" block variant="secondary" onClick={() => decide("deny")} loading={busy === "deny"} disabled={!!busy}>
                Don't allow
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
